import React, { useState } from "react";
import axios from "axios";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Container from "components/Container";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import config from "../../../../config/config";
import useProductData from "Hooks/useProductData";
import { generateProductCategoryUrl } from "Utilities/urlHelpers";
import { getSearchOptionLabel } from "Utilities/searchOptionLabel";

const filterOptions = createFilterOptions({
  ignoreCase: true,
  limit: 10,
});

const GlobalSearchBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const { data: suggestions, loading: suggestionsLoading } = useProductData(
    null,
    null,
    null,
    searchInput,
    null
  );

  const searchFieldLabel =
    t("searchBoxHero.searchBoxLabel", {
      defaultValue: t("searchBoxHero.serachBoxLabel"),
    }) || t("searchBoxHero.serachBoxLabel");

  const handleOptionSelect = async (event, value) => {
    if (!value) return;

    const groupId = value.groupid ?? value.groupId;
    const partNumber =
      value.params?.PartNumber ?? value.params?.partNumber ?? null;

    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/group/${groupId}/name`
      );
      const groupName = response.data.groupName;
      const path = generateProductCategoryUrl(groupName);
      const query =
        partNumber != null && String(partNumber).length > 0
          ? `?partNumber=${encodeURIComponent(String(partNumber))}`
          : "";
      navigate(`${path}${query}`);
    } catch (error) {
      console.error("GlobalSearchBar: failed to resolve group URL", error);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 2, md: 3 },
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Container>
        <Box
          sx={{
            maxWidth: 720,
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            {t("searchBoxHero.title")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t("searchBoxHero.subtext_1")}
          </Typography>
          <Autocomplete
            options={Array.isArray(suggestions) ? suggestions : []}
            filterOptions={filterOptions}
            loading={suggestionsLoading}
            loadingText={t("searchBoxHero.searching")}
            noOptionsText={
              suggestionsLoading
                ? t("searchBoxHero.searching")
                : t("searchBoxHero.noOptions")
            }
            getOptionLabel={(option) =>
              option ? getSearchOptionLabel(option) : ""
            }
            inputValue={searchInput}
            onInputChange={(event, value) => {
              setSearchInput(value ?? "");
            }}
            onChange={handleOptionSelect}
            isOptionEqualToValue={(option, value) =>
              option?.params?.ItemCode != null &&
              value?.params?.ItemCode != null &&
              option.params.ItemCode === value.params.ItemCode
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <Typography sx={{ display: "flex", alignItems: "center" }}>
                    <SearchIcon sx={{ marginRight: "0.5rem" }} />
                    {searchFieldLabel}
                  </Typography>
                }
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                }}
              />
            )}
            sx={{
              width: "100%",
              textAlign: "left",
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
              },
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default GlobalSearchBar;
